import ChevronRightSvg from "./ChevronRightSvg";
import ArrowRightSvg from "./ArrowRightSvg";
import BackCrossSvg from "./BackCrossSvg";
import ArrowRightCircleSvg from "./ArrowRightCircleSvg";
import { useCallback, useMemo, useState, useRef, useEffect } from "preact/hooks";
import ChevronLeftSvg from "./ChevronLeftSvg";
import styleClasses from '../menuItemStyles';
import dialogManager from '../../../utils/dialogManager';

type RenderStates = 'undecided' | 'active' | 'overview' | 'back' | 'current';

export default ( props ) => {
	const {
		activeID,
		ID,
		title,
		hasChildren,
		handleSubmenuToggle,
		columnHasOverviewItem = false,
		url,
		depth = 0,
		isOverview = false,
		isBack = false,
		isPartOfOpeningPath,
		setCurrentPageItemRef,
	} = props;

	const currentPageItemRef = useRef( null );

	const [ renderState, setRenderState ] = useState<RenderStates>(
		'undecided',
	);

	useMemo( () => {
		/**
		 * It is important that only one of these render states is applied, because otherwise
		 * there could be some duplicate tailwind classes in the html result.
		 *
		 * Therefore current, back and overview should come first, because an item could be
		 * the active/current one and a back button at the same time.
		 */
		if ( ! hasChildren && isPartOfOpeningPath ) return setRenderState( 'current' );
		if ( isBack ) return setRenderState( 'back' );
		if ( isOverview ) return setRenderState( 'overview' );
		if ( ID === activeID ) return setRenderState( 'active' );
		return setRenderState( 'undecided' );
	}, [ hasChildren, isPartOfOpeningPath, isBack, isOverview, ID, activeID ] );

	const renderChevron = () => {
		const iconSize = {
			width: '',
			height: '',
		};

		if ( depth === 0 || ( ( depth >= 1 && depth <= 10 ) && isBack ) ) {
			iconSize.width  = 'w-[23px]';
			iconSize.height = 'h-[35px]';
		} else {
			iconSize.width  = 'w-[15px]';
			iconSize.height = 'h-[23px]';
		}

		const classes = `inline mx-[10px]
			group-hover:text-identity-yellow-40
			${iconSize.width} ${iconSize.height}
            ${( renderState === 'active' || renderState === 'back' ) ? 'text-identity-yellow' : ''}
        `;

		if ( isOverview ) return (
			<>
				<ArrowRightSvg
					className="w-[32px] h-[23px] inline mx-[10px] group-hover:hidden"
				/>
				<ArrowRightCircleSvg className={`
                    w-[32px] h-[32px] mx-[10px] hidden text-identity-yellow-40 group-hover:inline
                    ${renderState === 'current' ? 'group-hover:text-identity-darkblue' : ''}
                `} />
		    </>
		);

		if ( ! hasChildren ) return <></>;

		switch ( renderState ) {
		case 'active':
		case 'back':
			return (
				<>
					<ChevronLeftSvg className={`${classes} block group-hover:hidden`} />
					<BackCrossSvg
						className={`
                            mx-[10px]
                            relative -left-[6px]
                            text-identity-yellow-40
                            hidden group-hover:block
                            ${ depth === 0 || ( ( depth === 1 || depth === 2 ) && isBack )
					            ? 'w-[32px] h-[32px]'
					            : 'w-[23px] h-[23px]'}
                            `}
					/>
				</>
			);
		default:
			return <ChevronRightSvg className={classes} />;
		}
	};

	const determineFontColors = useCallback( () => {
		if ( renderState === 'current' ) {
			return 'text-identity-darkblue';
		}

		if ( ID !== activeID && activeID !== -1 ) {
			return 'text-identity-blue-60 hover:text-pure-white';
		}

		if ( ID === activeID ) {
			return 'text-identity-yellow hover:text-identity-yellow-40';
		}

		return 'text-pure-white hover:text-identity-yellow-40';
	}, [ ID, activeID, renderState ] );

	useEffect( () => {
		if ( currentPageItemRef.current && isPartOfOpeningPath ) {
			setCurrentPageItemRef( currentPageItemRef.current );
		}
	}, [ currentPageItemRef, isPartOfOpeningPath, setCurrentPageItemRef ] );

	return (
		<li key={ID}
			className={`
                w-full first:border-t-0 first:py-0 first:my-0
		        border-identity-blue-80 border-t
			    ${columnHasOverviewItem && '[&:nth-child(2)]:border-t-4'}
			    ${depth === 0 && 'md:border-t-0 pt-[5px] mt-[5px] md:pt-0 md:mt-[8px]' }
            `}>
			{hasChildren ?
				<button
					ref={currentPageItemRef}
					data-menuid={ID}
					onClick={handleSubmenuToggle}
					className={`
                        ${styleClasses[`${( depth === 0 || depth === 1 ) ? depth : "default"}`][renderState]}
                        ${determineFontColors()}
                    `}>
					<span className={`
                        flex-1
                        ${depth !== 0 ? 'px-[10px]' : ''}
                    `}>
						{title}
					</span>
					{renderChevron()}
				</button>
				:
				<a
					ref={currentPageItemRef}
					href={url}
					onClick={() => {
						const urlStr: string = url.toString();
						if ( ! urlStr.replace( /#.*$/, '' ).includes( window.location.toString().replace( /#.*$/, '' ) ) ) return;
						const allDialogs = document.querySelectorAll( 'dialog' );
						for ( const aDialog of allDialogs ) {
							dialogManager.closeDialog( aDialog );
						}
						window.location.assign( url );
						window.location.reload();
					}}
					className={`
						${styleClasses[`${( depth === 0 || depth === 1 ) ? depth : "default"}`][renderState]}
						${determineFontColors()}
					`}>
					<span
						className={`
							flex-1
							${depth !== 0 ? 'px-[10px]' : ''}
							${depth === 0 && renderState === 'current' ? 'px-[10px]' : ''}
						`}>
						{title}
					</span>
					{renderChevron()}
				</a>
			}
		</li>
	);

};
